import Link from 'next/link'
import BrandName from '@/app/BrandName'

export const metadata = {
  title: 'How it works',
  description: 'How Ask QIEN routes quality issues from the production floor to your specialists — with AI triage, knowledge-base-grounded draft responses, and automatic knowledge capture.',
  openGraph: {
    title: 'How Ask QIEN works',
    description: 'AI triage, expert-guided responses, and automatic knowledge capture — built for pharmaceutical, medical device, and food manufacturing.',
    url: 'https://quality-intelligence.co/about',
    type: 'website',
  },
  alternates: {
    canonical: 'https://quality-intelligence.co/about',
  },
}

export default function AboutPage() {
  return (
    <div className="bg-white dark:bg-zinc-950 text-zinc-950 dark:text-zinc-100">

      {/* Header */}
      <section className="mx-auto max-w-3xl px-6 pt-16 pb-12">
        <h1 className="text-4xl font-bold tracking-tight text-zinc-950 dark:text-white">
          About <BrandName />
        </h1>
        <p className="mt-5 text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed">
          <BrandName /> is an AI-augmented expert support platform built for quality and operations teams
          in regulated manufacturing. It replaces ad-hoc email threads and tribal knowledge with a
          structured, auditable system that gets faster and smarter with every ticket closed.
        </p>
      </section>

      {/* The problem */}
      <section className="border-t border-zinc-100 dark:border-zinc-800">
        <div className="mx-auto max-w-3xl px-6 py-14">
          <h2 className="text-xl font-bold text-zinc-950 dark:text-white mb-5">The problem we are solving</h2>
          <div className="prose prose-zinc dark:prose-invert max-w-none text-zinc-600 dark:text-zinc-400 text-sm leading-7 space-y-4">
            <p>
              In pharmaceutical, medical device, and food manufacturing, production issues are high-stakes.
              A failed validation run or an equipment deviation can halt an entire line — and the cost of
              downtime is measured in thousands of pounds per hour, not to mention the regulatory risk.
            </p>
            <p>
              Yet most quality teams still manage these issues over email. A line manager fires off a
              vague message. It lands in a shared inbox. The right expert may not see it until tomorrow.
              When they do, they have to ask three follow-up questions before they can even start to help.
              The response they write will never be read by the next person who hits the same problem.
            </p>
            <p>
              The knowledge that exists inside your most experienced people&apos;s heads — the hard-won
              understanding of why Sealing Line 3 always drifts after a Saturday shutdown, or which
              supplier deviation paperwork actually matters for an FDA audit — stays invisible until
              someone thinks to ask the right person at the right time.
            </p>
            <p>
              <BrandName /> was built to fix this.
            </p>
          </div>
        </div>
      </section>

      {/* How it works — detail */}
      <section className="border-t border-zinc-100 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/50">
        <div className="mx-auto max-w-3xl px-6 py-14">
          <h2 className="text-xl font-bold text-zinc-950 dark:text-white mb-8">How the platform works</h2>
          <div className="space-y-10">

            <div>
              <h3 className="font-semibold text-zinc-950 dark:text-white mb-2">Structured intake, not open emails</h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                Line managers and operators submit issues through a structured form — not a free-form email.
                They specify the department, affected equipment or system, date of occurrence, declared priority,
                and a full description of what happened. The form is guided and consistent, so experts always
                receive comparable, structured information rather than a stream-of-consciousness email at 2am.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-zinc-950 dark:text-white mb-2">AI completeness check and clarification</h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                Immediately after submission, an AI agent scores the ticket for information completeness across
                six dimensions: problem description, affected equipment, observed symptoms, timeline, attempted
                remedies, and business impact. If the score falls below your configured threshold, the system
                automatically asks targeted clarifying questions and waits for the response before routing to an expert.
                No more back-and-forth once the expert has the ticket.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-zinc-950 dark:text-white mb-2">AI triage: urgency, sentiment, and tagging</h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                Once the ticket is complete, three AI agents run in parallel. The urgency and sentiment agent
                assesses how time-critical and emotionally-loaded the issue is, and sets a priority band
                (low, medium, high, critical). The auto-tagging agent links the ticket to relevant entries in
                your knowledge base, making it instantly searchable and improving future retrieval.
                The summarization agent produces a concise expert-facing brief so your specialist can
                understand the situation in thirty seconds rather than three minutes.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-zinc-950 dark:text-white mb-2">AI-drafted response, grounded in your knowledge base</h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                When the expert is ready to respond, they can request a draft. The AI searches your
                organization&apos;s knowledge base for semantically relevant entries — not keyword matches,
                but meaning — and writes a response draft citing the sources it used. The expert sees
                the draft alongside the sources and any identified knowledge gaps. They can edit freely
                before sending. The goal is never to replace the expert&apos;s judgment; it is to remove
                the blank-page problem so they can focus on what they actually know.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-zinc-950 dark:text-white mb-2">Knowledge capture as a side effect of good work</h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                After every expert response, the AI reviews the exchange and proposes a knowledge base update
                if the resolution contains information not already captured. Your experts review and approve
                proposals before they are published. Over time, the knowledge base becomes a living record
                of how your organization solves the problems it actually faces — not a library of templates
                that nobody reads.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Designed for regulated environments */}
      <section className="border-t border-zinc-100 dark:border-zinc-800">
        <div className="mx-auto max-w-3xl px-6 py-14">
          <h2 className="text-xl font-bold text-zinc-950 dark:text-white mb-5">Designed for regulated environments</h2>
          <div className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed space-y-4">
            <p>
              Every action in <BrandName /> is written to an immutable audit log: who submitted what, when,
              which AI agents ran and what they produced, which expert responded, and what edits were made.
              The knowledge base maintains a full revision history. This is not an afterthought — it is
              built into the data model from the ground up.
            </p>
            <p>
              Data processing consent is configurable per tenant. If your organization has restrictions on
              sending production data to an AI provider, you can disable AI processing while retaining
              the structured workflow. Expert teams can still receive structured tickets, respond, and
              build the knowledge base manually.
            </p>
            <p>
              The platform is available in English and Spanish, with no configuration required. Line managers
              and operators work in the language they are most comfortable with — which matters on production
              floors where submitting an issue clearly and quickly is the point. For manufacturing teams with
              mixed-language workforces, it means issues get raised accurately, not deferred because someone
              was not confident writing in a second language.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-zinc-100 dark:border-zinc-800">
        <div className="mx-auto max-w-3xl px-6 py-14 flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between">
          <div>
            <h2 className="text-lg font-bold text-zinc-950 dark:text-white">Want to see it with your own issues?</h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">We run a live walkthrough using real scenarios from your industry.</p>
          </div>
          <Link
            href="/contact"
            className="shrink-0 rounded-lg bg-[var(--brand-green)] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[var(--brand-green-hover)]"
          >
            Book a walkthrough
          </Link>
        </div>
      </section>

    </div>
  )
}
